<template>
  <div
    ref="root"
    class="release-sections"
    :style="{ paddingBottom: `${scrollGuard}px` }"
  >
    <div
      v-for="section in sections"
      :key="section._key"
      class="_section"
    >
      <h4 class="_heading">
        <button
          type="button"
          class="_header"
          :aria-expanded="isExpanded(section._key)"
          :aria-controls="panelId(section._key)"
          @click="toggle(section._key)"
        >
          <m-icon
            :class="['_chevron', { '-expanded': isExpanded(section._key) }]"
            type="lucide-chevron-right"
            size="16"
            aria-hidden="true"
          />
          {{ section.title }}
        </button>
      </h4>
      <ul
        v-show="isExpanded(section._key)"
        :id="panelId(section._key)"
        class="_items"
      >
        <li
          v-for="item in sortedItems(section)"
          :key="item._key"
          class="_item"
        >
          <template v-if="item.category">
            <release-category-tag :category="item.category" />{{ ' ' }}
          </template><portable-text
            :value="item.content"
            :components="portableTextComponents"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import InlineBlockComponent from '@/release-notes/InlineBlockComponent.vue';
import ReleaseCategoryTag from '@/release-notes/ReleaseCategoryTag.vue';
import { PortableText } from '@portabletext/vue';
import { collapseDeficit, remainingGuard } from '@/release-notes/scroll-guard';
import { onBeforeUnmount, ref, useId } from 'vue';
import { portableTextMarks } from '@/release-notes/portable-text-marks';
import { releaseCategoryLabel } from '@/release-notes/release-categories';

defineProps({
  sections: {
    type: Array,
    required: true,
  },
});

const root = ref(null);
const expanded = ref([]);
const scrollGuard = ref(0);
const uid = useId();

let scroller = null;

const panelId = (key) => `${uid}-${key}`;

const isExpanded = (key) => expanded.value.includes(key);

const findScroller = () => {
  let el = root.value?.parentElement;
  while (el) {
    if (['auto', 'scroll'].includes(getComputedStyle(el).overflowY)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
};

const shrinkGuard = () => {
  scrollGuard.value = remainingGuard(scrollGuard.value, scroller);
  if (scrollGuard.value === 0) {
    scroller.removeEventListener('scroll', shrinkGuard);
  }
};

const reserveCollapsedHeight = (key) => {
  scroller = scroller ?? findScroller();
  if (scroller === null) {
    return;
  }
  const { top, height } = document.getElementById(panelId(key)).getBoundingClientRect();
  if (top < scroller.getBoundingClientRect().top) {
    return;
  }
  const deficit = collapseDeficit(height, scroller);
  if (deficit > 0) {
    scrollGuard.value += deficit;
    scroller.addEventListener('scroll', shrinkGuard, { passive: true });
  }
};

const toggle = (key) => {
  if (!isExpanded(key)) {
    expanded.value = [...expanded.value, key];
    return;
  }
  reserveCollapsedHeight(key);
  expanded.value = expanded.value.filter((next) => next !== key);
};

onBeforeUnmount(() => {
  scroller?.removeEventListener('scroll', shrinkGuard);
});

const sortedItems = (section) => [...(section.items ?? [])].sort(
  (a, b) => (!a.category - !b.category)
    || releaseCategoryLabel(a.category).localeCompare(releaseCategoryLabel(b.category)),
);

const portableTextComponents = {
  block: { normal: InlineBlockComponent },
  marks: portableTextMarks,
};
</script>

<style scoped lang="scss" type="text/scss">
  .release-sections {
    padding-top: 2.6rem;
    border-top: 1px solid $border-color-dark;

    ._section + ._section {
      margin-top: .4rem;
    }

    ._heading {
      margin: 0;
    }

    ._header {
      display: flex;
      gap: .8rem;
      align-items: center;
      width: calc(100% + 1.2rem);
      height: 3.15rem;
      padding: 0 .6rem;
      margin: 0 -.6rem;
      font-size: $font-size-4;
      font-weight: $font-weight-medium;
      line-height: 2rem;
      color: $font-color-primary;
      text-align: left;
      cursor: pointer;
      background-color: transparent;
      border: 0;
      border-radius: .8rem;

      &:hover {
        background-color: $hover-color;
      }

      &:focus-visible {
        outline: 2px solid $primary-color;
        outline-offset: -2px;
      }
    }

    ._chevron {
      flex: 0 0 auto;
      color: $font-color-secondary;
      transition: transform .12s ease;

      &.-expanded {
        transform: rotate(90deg);
      }
    }

    ._items {
      padding: .6rem 0 1rem 2.4rem;
      margin: 0;
      line-height: 2.2rem;
    }

    ._item + ._item {
      margin-top: .8rem;
    }

    @media (prefers-reduced-motion: reduce) {
      ._chevron {
        transition: none;
      }
    }
  }
</style>
